"use client";

import { useState, useMemo } from "react";
import { useTranslations } from "next-intl";
import { Link } from "@/navigation";
import { CaretDown, CloudArrowUp } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import GetInstantQuoteButton from "@/components/NewVersion/GetInstantQuoteButton";

const generateFaqData = (count: number, prefix = "") =>
	Array.from({ length: count }, (_, i) => ({
		id: `q${i + 1}`,
		questionKey: prefix ? `${prefix}Q${i + 1}` : `q${i + 1}`,
		answerKey: prefix ? `${prefix}A${i + 1}` : `a${i + 1}`,
	}));

const PAGE_CONFIG: Record<
	string,
	{ namespace: string; faqCount: number; titleKey: string; moreKey: string }
> = {
	"cnc-machining": {
		namespace: "new-cnc-machining",
		faqCount: 11,
		titleKey: "faqsTitle",
		moreKey: "faqsSeeMoreFAQs",
	},
	"cnc-milling": {
		namespace: "new-cnc-milling",
		faqCount: 9,
		titleKey: "faqsTitle",
		moreKey: "faqsSeeMoreFAQs",
	},
	"5-axis-cnc-machining": {
		namespace: "new-5-axis-cnc-machining",
		faqCount: 7,
		titleKey: "faqsTitle",
		moreKey: "faqsSeeMoreFAQs",
	},
	"gear-machining": {
		namespace: "new-gear-machining",
		faqCount: 9,
		titleKey: "faqsTitle",
		moreKey: "faqsSeeMoreFAQs",
	},
	"precision-machining": {
		namespace: "new-precision-machining",
		faqCount: 10,
		titleKey: "faqsTitle",
		moreKey: "faqsSeeMoreFAQs",
	},
};

const DEFAULT_CONFIG = {
	namespace: "manufacturingFAQs",
	faqCount: 8,
	titleKey: "title",
	moreKey: "seeMoreFAQs",
};

interface FAQDataItem {
	id: string;
	questionKey: string;
	answerKey: string;
}

function FAQItem({
	faq,
	t,
	isOpen,
	onToggle,
}: {
	faq: FAQDataItem;
	t: (key: string) => string;
	isOpen: boolean;
	onToggle: () => void;
}) {
	return (
		<div className="">
			<button
				onClick={onToggle}
				className="group flex w-full items-center justify-between py-5 text-left transition-colors hover:text-themePrimary400"
				aria-expanded={isOpen}
			>
				<span className="pr-4 text-base font-semibold text-black transition-colors group-hover:text-themePrimary400 sm:text-lg">
					{t(faq.questionKey)}
				</span>
				<CaretDown
					size={20}
					weight="bold"
					className={`flex-shrink-0 text-gray-900 transition-transform duration-300 ${
						isOpen ? "rotate-180" : ""
					}`}
				/>
			</button>
			<div
				className={`overflow-hidden transition-all duration-300 ease-in-out ${
					isOpen ? "max-h-[500px] pb-5" : "max-h-0"
				}`}
			>
				<p className="text-sm leading-relaxed text-black md:text-base">{t(faq.answerKey)}</p>
			</div>
		</div>
	);
}

export interface ManufacturingFAQsSplitTitle {
	line1Key: string;
	line2Key: string;
}

interface ManufacturingFAQsProps {
	slug?: string;
	className?: string;
	translationNamespace?: string;
	faqCount?: number;
	titleKey?: string;
	moreKey?: string;
	splitTitleWithSuffixLine?: ManufacturingFAQsSplitTitle;
	getInstantQuoteHref?: string;
	seeMoreFaqsHref?: string;
}

export default function ManufacturingFAQs({
	slug,
	className,
	translationNamespace,
	faqCount,
	titleKey,
	moreKey,
	getInstantQuoteHref,
	seeMoreFaqsHref,
	splitTitleWithSuffixLine,
}: ManufacturingFAQsProps) {
	const [openIndex, setOpenIndex] = useState<number | null>(null);

	const baseConfig = slug && PAGE_CONFIG[slug] ? PAGE_CONFIG[slug] : DEFAULT_CONFIG;
	const config = {
		namespace: translationNamespace || baseConfig.namespace,
		faqCount: faqCount || baseConfig.faqCount,
		titleKey: titleKey || baseConfig.titleKey,
		moreKey: moreKey || baseConfig.moreKey,
	};
	const isCustomPage = translationNamespace || (slug && PAGE_CONFIG[slug]);

	const faqsData = useMemo(
		() => generateFaqData(config.faqCount, isCustomPage ? "faqs" : ""),
		[config.faqCount, isCustomPage],
	);

	const t = useTranslations(config.namespace);
	const tBase = useTranslations("base");

	const handleToggle = (index: number) => {
		setOpenIndex(openIndex === index ? null : index);
	};

	return (
		<section className={cn("bg-[#f6f6f6] py-8 sm:py-10 md:py-12 lg:py-16", className)}>
			<div className="container">
				<div className="flex flex-col gap-10 lg:flex-row lg:items-stretch lg:gap-16">
					<div className="flex w-full min-w-0 flex-col justify-between lg:w-[40%] lg:max-w-[40%] lg:shrink-0">
						<div className="mb-8 min-w-0">
							{splitTitleWithSuffixLine ? (
								<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
									<span className="block break-words">{t(splitTitleWithSuffixLine.line1Key)}</span>
									<span className="mt-1 flex min-w-0 flex-wrap items-baseline gap-2 sm:mt-1.5 sm:gap-2.5">
										<span className="min-w-0 break-words">{t(splitTitleWithSuffixLine.line2Key)}</span>
										<span
											className="h-0.5 w-14 shrink-0 bg-themePrimary400 sm:w-24 md:w-32 lg:w-36"
											aria-hidden
										/>
									</span>
								</h2>
							) : (
								<h2 className="mb-3 flex min-w-0 max-w-full flex-wrap items-end gap-x-2 gap-y-1 text-2xl font-bold leading-tight text-black sm:mb-4 sm:gap-x-3 sm:text-[30px] md:text-4xl">
									<span className="min-w-0 break-words">{t(config.titleKey)}</span>
									<span
										className="mb-[0.18em] h-[2px] w-12 shrink-0 bg-themePrimary400 sm:w-16 md:w-20"
										aria-hidden
									/>
								</h2>
							)}
						</div>

						<div className="flex flex-col gap-3 pb-20 max-md:pb-4 sm:flex-row sm:gap-4">
							{getInstantQuoteHref ? (
								getInstantQuoteHref.startsWith("http") ? (
									<a
										href={getInstantQuoteHref}
										target="_blank"
										rel="noopener noreferrer"
										className="group inline-flex items-center justify-center gap-2 rounded bg-themePrimary400 px-4 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-opacity-80 hover:text-white sm:px-5 sm:py-3 md:px-6 md:py-3.5 md:text-base"
									>
										<CloudArrowUp className="h-4 w-4 md:h-[18px] md:w-[18px]" weight="fill" />
										{t("getInstantQuote")}
									</a>
								) : (
									<Link
										href={getInstantQuoteHref}
										className="group inline-flex items-center justify-center gap-2 rounded bg-themePrimary400 px-4 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-opacity-80 hover:text-white sm:px-5 sm:py-3 md:px-6 md:py-3.5 md:text-base"
									>
										<CloudArrowUp className="h-4 w-4 md:h-[18px] md:w-[18px]" weight="fill" />
										{t("getInstantQuote")}
									</Link>
								)
							) : (
								<GetInstantQuoteButton />
							)}
							{seeMoreFaqsHref ? (
								seeMoreFaqsHref.startsWith("http") ? (
									<a
										href={seeMoreFaqsHref}
										target="_blank"
										rel="noopener noreferrer"
										className="group inline-flex w-full items-center justify-center gap-2 rounded bg-mainColor px-4 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-opacity-80 hover:text-white sm:w-fit sm:px-5 sm:py-3 md:px-10 md:py-3.5 md:text-base"
									>
										{t(config.moreKey)}
									</a>
								) : (
									<Link
										href={seeMoreFaqsHref}
										className="group inline-flex w-full items-center justify-center gap-2 rounded bg-mainColor px-4 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-opacity-80 hover:text-white sm:w-fit sm:px-5 sm:py-3 md:px-10 md:py-3.5 md:text-base"
									>
										{t(config.moreKey)}
									</Link>
								)
							) : (
								<Link
									href="/faqs"
									className="group inline-flex w-full items-center justify-center gap-2 rounded bg-mainColor px-4 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-opacity-80 hover:text-white sm:w-fit sm:px-5 sm:py-3 md:px-10 md:py-3.5 md:text-base"
								>
									{t(config.moreKey)}
								</Link>
							)}
						</div>
					</div>

					<div className="w-full min-w-0 lg:w-[60%] lg:min-w-0">
						<div className="rounded-xl">
							{faqsData.map((faq, index) => (
								<FAQItem
									key={faq.id}
									faq={faq}
									t={t}
									isOpen={openIndex === index}
									onToggle={() => handleToggle(index)}
								/>
							))}
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
